import { useEffect, useReducer } from 'react'
import deepEqual from 'fast-deep-equal/es6'
import { usePrevious } from './usePrevious'
import logger from 'use-reducer-logger'
import {
  getItem,
  idKey,
  setItem,
  findAnyVersionedItem,
} from '../lib/localStorage'
import { isLocalhost } from '../lib/utils'
import { getDecodedHashParam, HASH_PARAM_KEY_ID } from '../lib/hashUtils'
import { get } from 'lodash'

export const usePersistedReducer = <State, Action>(
  reducer: (state: State, action: Action) => State,
  initialState: State,
  storageKey: string,
  shouldEncrypt = true
) => {
  const [state, dispatch] = useReducer(
    isLocalhost() ? logger(reducer) : reducer,
    initialState,
    init
  )
  const prevState = usePrevious(state)

  function init(): State {
    // Check if user arrived via shared link
    const sharedUserId = getDecodedHashParam(HASH_PARAM_KEY_ID)
    const localStorageUserId = getItem(idKey)
      ? JSON.parse(getItem(idKey) || '{}').id
      : null

    // If shared link with DIFFERENT userId than localStorage, use initialState
    if (sharedUserId && sharedUserId !== localStorageUserId) {
      // User came from a shared link with different ID, Firebase will provide the correct state
      return initialState
    }

    // If user has a userId in localStorage, they might be continuing their game
    const hasUserId = !!getItem(idKey)
    if (hasUserId && storageKey !== idKey) {
      // IMPORTANT: Preserve localStorage stats for THIS userId
      // Firebase will update with the correct stats for this userId
      // But until Firebase loads, we show the cached stats to avoid flickering
      const stringState =
        getItem(storageKey) ?? findAnyVersionedItem(storageKey)
      if (stringState) {
        try {
          const savedState = JSON.parse(stringState) as any
          // Only preserve stats if the userId matches
          const savedUserId = savedState?.id
          const currentUserId = getItem(idKey)
            ? JSON.parse(getItem(idKey) || '{}').id
            : null

          if (savedUserId === currentUserId) {
            // Same user, preserve their stats
            return {
              ...initialState,
              stats: savedState.stats ?? (initialState as any).stats,
            } as State
          } else {
            // Different user (URL changed), don't preserve old stats
            return initialState
          }
        } catch (error) {
          return initialState
        }
      }
      return initialState
    }

    // First try current version, then fall back to any previous version
    const stringState = getItem(storageKey) ?? findAnyVersionedItem(storageKey)
    if (stringState) {
      try {
        return JSON.parse(stringState)
      } catch (error) {
        return initialState
      }
    } else {
      return initialState
    }
  }

  useEffect(() => {
    const stateEqual = deepEqual(prevState, state)
    if (!stateEqual) {
      const stringifiedState = JSON.stringify(state)
      try {
        if (get(state, idKey)) {
          setItem(
            idKey,
            JSON.stringify({ id: get(state, idKey) }),
            undefined,
            false
          )
        }
        setItem(storageKey, stringifiedState, undefined, shouldEncrypt)
      } catch (err) {
        console.log('Debug', err)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, storageKey])

  return { state, dispatch }
}
